import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from "react";
import { useAgentChatContext, useAgentDraft } from "../chat/agentChatContext";
import type { useAgentBudget } from "../chat/useAgentBudget";
import type { ChatEntry } from "../chat/useAgentChat";
import { useAgentSend } from "../chat/useAgentSend";
import {
  playListenCue,
  primeListenCue,
  startThinkingCue,
  stopThinkingCue,
} from "../chat/listenCue";
import { releaseVoiceWakeLock, requestVoiceWakeLock } from "../chat/voiceWakeLock";
import {
  getGreetingSpeaking,
  getSpeakingEntryId,
  getVoiceDialogActive,
  pauseSpeech,
  resumeSpeech,
  setVoiceDialogActive,
  speakOnce,
  stopSpeech,
  replyHasVisual,
  subscribeGreeting,
  subscribeSpeech,
  toSpeakableText,
} from "../chat/speechPlayback";
import { scrollTranscriptToTarget } from "../chat/scrollTranscriptToVisual";
import {
  appendTranscript,
  describeVoiceState,
  useVoiceInput,
  type VoiceInput,
} from "@/shared/hooks/useVoiceInput";
import {
  classifyVoiceDialogUtterance,
  isVoiceDialogEcho,
  VOICE_DIALOG_ECHO_TAIL_MS,
} from "./voiceDialogIntent";
import {
  VOICE_DIALOG_GOODBYE,
  VOICE_DIALOG_MIC_BLOCKED,
  VOICE_HOLD_PHRASE,
  VOICE_PHASE_LABEL,
  VOICE_RESUME_LABEL,
  markHoldPhraseSpoken,
  resetHoldPhraseCooldown,
  shouldSpeakHoldPhrase,
  voiceDialogHasTranscript,
  voiceDialogOffersHelp,
  voiceDialogOpeningLine,
  voiceDialogPhase,
  voiceDialogSpokenReply,
  voiceUtteranceGapMs,
  type VoiceDialogPhase,
  type VoiceLaunchActivity,
  type VoiceLaunchCommand,
} from "./voiceLaunch";

type AgentBudgetState = ReturnType<typeof useAgentBudget>;

/**
 * Shortest spacing between automatic mic reopenings after a silent natural end.
 * Normal `no-speech` timeouts are seconds apart, so this only breaks a tight
 * loop (e.g. a denied-permission recognizer that ends immediately every time).
 */
const MIN_AUTO_RESTART_INTERVAL_MS = 1_000;

/**
 * Recognizer errors that will not fix themselves on a retry: a blocked
 * permission or a missing device. A dialog ends on these instead of reopening
 * the mic forever. Transient codes (`no-speech`, `network`) stay on the
 * auto-restart path.
 */
const FATAL_VOICE_ERRORS: ReadonlySet<string> = new Set([
  "not-allowed",
  "service-not-allowed",
  "audio-capture",
]);

function greetingServerSnapshot(): boolean {
  return false;
}

function idleSpeakingEntry(): string | null {
  return null;
}

type VoiceControlRefs = Readonly<{
  current: { start: () => void; stop: () => void; listening: boolean };
}>;

/**
 * Open the dialog mic after the turn cue. The cue finishes before recognition
 * starts, so the microphone does not transcribe it. A suspended or paused
 * dialog skips both. The thinking pulse stops first so it never overlaps the cue.
 */
function beginListening(
  suspendedRef: Readonly<{ current: boolean }>,
  pausedRef: Readonly<{ current: boolean }>,
  voiceModeRef: Readonly<{ current: boolean }>,
  autoSendRef: { current: boolean },
  commandListenRef: { current: boolean },
  voiceRef: VoiceControlRefs,
): void {
  stopThinkingCue();
  if (suspendedRef.current || pausedRef.current || !voiceModeRef.current) {
    return;
  }
  commandListenRef.current = false;
  autoSendRef.current = true;
  if (voiceRef.current.listening) {
    playListenCue(() => undefined);
    return;
  }
  playListenCue(() => {
    if (suspendedRef.current || pausedRef.current || !voiceModeRef.current) {
      return;
    }
    commandListenRef.current = false;
    autoSendRef.current = true;
    voiceRef.current.start();
  });
}

/**
 * Hear pause / resume / end while the assistant is talking or the dialog is
 * paused. No listen cue — that would be transcribed and is the user's turn.
 */
function beginCommandListening(
  suspendedRef: Readonly<{ current: boolean }>,
  voiceModeRef: Readonly<{ current: boolean }>,
  autoSendRef: { current: boolean },
  commandListenRef: { current: boolean },
  voiceRef: VoiceControlRefs,
): void {
  if (suspendedRef.current || !voiceModeRef.current) {
    return;
  }
  commandListenRef.current = true;
  autoSendRef.current = false;
  if (!voiceRef.current.listening) {
    voiceRef.current.start();
  }
}

function rememberSpokenLine(
  text: string,
  spokenLineRef: { current: string },
  echoTailTimerRef: { current: number | null },
): void {
  if (echoTailTimerRef.current !== null) {
    window.clearTimeout(echoTailTimerRef.current);
    echoTailTimerRef.current = null;
  }
  spokenLineRef.current = text;
}

function beginSpokenEchoTail(
  spokenLineRef: { current: string },
  echoTailTimerRef: { current: number | null },
): void {
  if (echoTailTimerRef.current !== null) {
    window.clearTimeout(echoTailTimerRef.current);
  }
  echoTailTimerRef.current = window.setTimeout(() => {
    spokenLineRef.current = "";
    echoTailTimerRef.current = null;
  }, VOICE_DIALOG_ECHO_TAIL_MS);
}

/** Speak a dialog line and listen for session commands over it. */
function speakDialogLine(
  text: string,
  onDone: () => void,
  spokenLineRef: { current: string },
  echoTailTimerRef: { current: number | null },
  startCommandListen: () => void,
): void {
  rememberSpokenLine(text, spokenLineRef, echoTailTimerRef);
  startCommandListen();
  speakOnce(text, () => {
    beginSpokenEchoTail(spokenLineRef, echoTailTimerRef);
    onDone();
  });
}

function dialogLive(
  suspendedRef: Readonly<{ current: boolean }>,
  pausedRef: Readonly<{ current: boolean }>,
  voiceModeRef: Readonly<{ current: boolean }>,
): boolean {
  return !suspendedRef.current && !pausedRef.current && voiceModeRef.current;
}

/**
 * A voice-dialog utterance that only controls the session is answered out
 * loud, or paused, and kept out of the chat. Classify before touching TTS so
 * an echo of the assistant cannot cut it off. Anything else on the user's turn
 * is sent to the assistant.
 */
function deliverSpokenUtterance(
  text: string,
  input: Readonly<{
    voiceMode: boolean;
    awaitingHelpOffer: boolean;
    paused: boolean;
    assistantTurn: boolean;
    spokenLine: string;
    sendText: (text: string) => void;
    leaveVoiceMode: () => void;
    pauseDialog: () => void;
    resumeDialog: () => void;
    interruptDialog: () => void;
    setAwaitingHelpOffer: (awaiting: boolean) => void;
  }>,
): void {
  if (!input.voiceMode) {
    stopSpeech();
    input.sendText(text);
    return;
  }
  if (!input.paused && input.spokenLine.length > 0 && isVoiceDialogEcho(text, input.spokenLine)) {
    return;
  }
  const intent = classifyVoiceDialogUtterance(text, {
    awaitingHelpOffer: input.awaitingHelpOffer,
    paused: input.paused,
    assistantTurn: input.assistantTurn,
  });
  if (intent === "pause") {
    input.pauseDialog();
    return;
  }
  if (intent === "resume") {
    input.resumeDialog();
    return;
  }
  if (intent === "interrupt") {
    // Barge-in while the assistant is voicing a line: stop the speech and hand
    // the mic back to the user for their turn.
    input.setAwaitingHelpOffer(false);
    input.interruptDialog();
    return;
  }
  if (intent === "end") {
    input.setAwaitingHelpOffer(false);
    stopSpeech();
    speakOnce(VOICE_DIALOG_GOODBYE, input.leaveVoiceMode);
    return;
  }
  if (input.paused) {
    return;
  }
  if (input.assistantTurn) {
    // While the assistant is talking, the command mic honors only stop/resume.
    // Anything else is ignored instead of barging in, so stray speech, noise,
    // or the assistant hearing itself on speakers cannot cut off playback.
    return;
  }
  input.setAwaitingHelpOffer(false);
  stopSpeech();
  input.sendText(text);
}

/** The reply to read aloud next: an assistant turn that streamed in during this
 * conversation, has finished, was not interrupted, and has not been spoken yet. */
function isUnspokenLiveReply(
  entry: Readonly<ChatEntry>,
  liveReplyIdsRef: Readonly<{ current: Set<string> }>,
  spokenReplyIdsRef: Readonly<{ current: Set<string> }>,
): boolean {
  return (
    entry.role === "assistant" &&
    liveReplyIdsRef.current.has(entry.id) &&
    !entry.streaming &&
    !entry.cancelled &&
    !spokenReplyIdsRef.current.has(entry.id)
  );
}

/** Shared refs/values the thinking-cue phase reads and writes. */
type ThinkingCueContext = Readonly<{
  listening: boolean;
  holdingRef: { current: boolean };
  autoSendRef: { current: boolean };
  pendingSendRef: { current: boolean };
  streamingRef: Readonly<{ current: boolean }>;
  voiceRef: VoiceControlRefs;
  commandListenRef: Readonly<{ current: boolean }>;
  suspendedRef: Readonly<{ current: boolean }>;
  voiceModeRef: Readonly<{ current: boolean }>;
  pausedRef: Readonly<{ current: boolean }>;
  spokenLineRef: { current: string };
  echoTailTimerRef: { current: number | null };
  startCommandListen: () => void;
}>;

/**
 * After the hold phrase is spoken, resume the quiet pulse — but only if the
 * dialog is still live, the assistant is still streaming, and the mic is not
 * already open for the user's turn.
 */
function onHoldPhraseSpoken(ctx: ThinkingCueContext): void {
  if (
    !dialogLive(ctx.suspendedRef, ctx.pausedRef, ctx.voiceModeRef) ||
    !ctx.streamingRef.current ||
    (ctx.voiceRef.current.listening && !ctx.commandListenRef.current)
  ) {
    return;
  }
  startThinkingCue();
}

/**
 * Drive the thinking cue while the assistant is streaming: on the first tick of
 * a turn, say the hold phrase (or skip it within the repeat window) and open the
 * command mic; afterwards keep the quiet pulse off while the mic is open and on
 * otherwise.
 */
function advanceThinkingCue(ctx: ThinkingCueContext): void {
  if (!ctx.holdingRef.current) {
    ctx.holdingRef.current = true;
    ctx.autoSendRef.current = false;
    ctx.pendingSendRef.current = false;
    if (shouldSpeakHoldPhrase()) {
      markHoldPhraseSpoken();
      speakDialogLine(
        VOICE_HOLD_PHRASE,
        () => onHoldPhraseSpoken(ctx),
        ctx.spokenLineRef,
        ctx.echoTailTimerRef,
        ctx.startCommandListen,
      );
    } else {
      // Within the repeat window: skip the spoken line so it does not stack
      // up across turns. Keep the command mic open and the quiet pulse going
      // so pause/stop still work and the user still hears that we are busy.
      ctx.startCommandListen();
      if (
        !(ctx.voiceRef.current.listening && !ctx.commandListenRef.current) &&
        !getGreetingSpeaking()
      ) {
        startThinkingCue();
      }
    }
  } else if ((ctx.listening || ctx.voiceRef.current.listening) && !ctx.commandListenRef.current) {
    stopThinkingCue();
  } else if (!getGreetingSpeaking()) {
    startThinkingCue();
  }
}

/** How a host surface wires the voice dialog into its own chrome and layout. */
export interface AgentVoiceDialogEnv {
  /**
   * The host surface is on screen. The docked drawer passes its open state; the
   * full-page workspace is always visible, so it passes `true`. Governs the
   * reveal-visual handoff and the idle-mic cleanup.
   */
  open: boolean;
  /** Another surface took over (the drawer when the full-screen dialog is up). */
  suspended?: boolean;
  /** Launcher-issued commands (FAB). The in-composer button drives the dialog otherwise. */
  voiceCommand?: VoiceLaunchCommand | null;
  /** Report dialog state so a launcher can mirror it on the floating controls. */
  onVoiceActivity?: (activity: VoiceLaunchActivity) => void;
  /** Make a visual visible when the host is not currently showing it (open the drawer). */
  onRevealVisual?: () => void;
  /** Scroll container for the transcript, so a visual can be brought into view. */
  transcriptRef: RefObject<HTMLDivElement | null>;
  /** Stop following the stream once a visual has been scrolled into place. */
  releaseStick: () => void;
  /** Daily budget state; the dialog never sends while exhausted. */
  budget: AgentBudgetState;
}

/** Everything a composer needs to render and drive the voice dialog. */
export interface AgentVoiceDialog {
  draft: string;
  setDraft: ReturnType<typeof useAgentDraft>[1];
  /** Composer send button: stops voice + read-aloud, then sends the draft. */
  send: () => void;
  /** Raw send for suggestions/feedback that should not touch the mic. */
  sendText: (text: string) => void;
  /** The single recognizer, shared by composer dictation and the dialog mic. */
  voice: VoiceInput;
  inputDisabled: boolean;
  sendDisabled: boolean;
  voiceDisabled: boolean;
  voicePhase: VoiceDialogPhase;
  paused: boolean;
  voiceConversationDisabled: boolean;
  activityTooltip: string;
  /** Start / pause / resume the dialog from the one composer button. */
  toggleVoiceDialog: () => void;
  /** Interrupt a reply and hand the mic back to the user. */
  listenInDialog: () => void;
  /** End the dialog entirely (silent teardown after goodbye or a fatal mic error). */
  leaveVoiceMode: () => void;
  /** Speak goodbye, then leave. Same path as a spoken "no". */
  endVoiceDialog: () => void;
}

/**
 * The voice-conversation state machine shared by both assistant surfaces (the
 * docked {@link AgentDrawer} and the full-page AiAssist workspace). It owns the
 * single {@link VoiceInput} recognizer, the read-aloud / listen / thinking
 * cues, and the pause/resume lifecycle, so the two surfaces behave identically
 * and never fight over the microphone.
 */
export function useAgentVoiceDialog(env: Readonly<AgentVoiceDialogEnv>): AgentVoiceDialog {
  const {
    open,
    suspended = false,
    voiceCommand = null,
    onVoiceActivity,
    onRevealVisual,
    transcriptRef,
    releaseStick,
    budget,
  } = env;
  const chat = useAgentChatContext();
  const [draft, setDraft] = useAgentDraft();
  const draftRef = useRef(draft);
  const pendingSendRef = useRef(false);
  /** Listening started by the floating mic: a pause sends, composer dictation does not. */
  const autoSendRef = useRef(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const voiceModeRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const [pausedPhase, setPausedPhase] = useState<VoiceDialogPhase>("thinking");
  /**
   * Bumped on every native recognizer end that queued a restart. The restart
   * effect depends on it as well as `voice.listening`, so a restart queued when
   * `listening` is already false flushes once the end arrives: an interim-edit
   * `abort({ silent: false })` flips `listening` synchronously before the native
   * end fires, so without this signal the effect would never re-run.
   */
  const [endSignal, setEndSignal] = useState(0);
  /** The recognizer end caused by pause must not send the draft. */
  const ignoreNextEndRef = useRef(false);
  /** Mic was open when the user paused, so play starts it again with the same draft. */
  const resumeListeningRef = useRef(false);
  /** Deadline of the utterance gap while the mic is open. */
  const gapDeadlineRef = useRef<number | null>(null);
  /** Milliseconds left on the utterance gap when the dialog was paused. */
  const gapRemainingRef = useRef<number | null>(null);
  /** The last spoken line asked a question, so a short no ends the dialog. */
  const helpOfferRef = useRef(false);
  /** Mic is open for session commands, not the user's turn. */
  const commandListenRef = useRef(false);
  /** The silent end should reopen the command mic, including while paused. */
  const pendingCommandRestartRef = useRef(false);
  /** Normalized-source text currently (or just) spoken, for echo filtering. */
  const spokenLineRef = useRef("");
  const echoTailTimerRef = useRef<number | null>(null);
  const deliverCommandRef = useRef<(text: string) => void>(() => undefined);
  const sendPendingVoiceDraftRef = useRef<() => void>(() => undefined);
  /** A silent natural end asked to reopen the mic once the recognizer is idle. */
  const pendingRestartRef = useRef(false);
  /** Timestamp of the last automatic reopen, used to break a tight restart loop. */
  const lastAutoRestartRef = useRef(0);
  /** True between mount and real unmount; a microtask distinguishes StrictMode. */
  const mountedRef = useRef(false);
  const suspendedRef = useRef(suspended);
  /** Assistant turns observed while streaming, so a loaded history is not read aloud. */
  const liveReplyIdsRef = useRef(new Set<string>());
  const spokenReplyIdsRef = useRef(new Set<string>());
  /** Replies whose visual was already offered. Closing the drawer does not show it again. */
  const revealedVisualIdsRef = useRef(new Set<string>());
  /** Reply to scroll into view once the drawer is open and the visual node exists. */
  const pendingVisualFocusIdRef = useRef<string | null>(null);
  /** The one wait line for this thinking stretch has already started. */
  const holdingRef = useRef(false);
  const sendTextRef = useRef<(text: string, options?: { interrupt?: boolean }) => void>(
    () => undefined,
  );
  const streamingRef = useRef(chat.streaming);
  const entriesRef = useRef(chat.entries);
  const budgetLevelRef = useRef(budget.level);
  /** Last seen conversation epoch, so a new/loaded chat can end a live dialog. */
  const conversationEpochRef = useRef(chat.conversationEpoch);
  const voice = useVoiceInput({
    onResult: (text) => {
      if (commandListenRef.current) {
        deliverCommandRef.current(text);
        return;
      }
      setDraft((current) => {
        const next = appendTranscript(current, text);
        draftRef.current = next;
        return next;
      });
    },
    onEnd: () => {
      if (ignoreNextEndRef.current) {
        ignoreNextEndRef.current = false;
        if (commandListenRef.current && voiceModeRef.current && !suspendedRef.current) {
          pendingCommandRestartRef.current = true;
          pendingRestartRef.current = true;
          setEndSignal((n) => n + 1);
        }
        return;
      }
      const shouldSend = pendingSendRef.current;
      const wasAutoListening = autoSendRef.current;
      const wasCommandListen = commandListenRef.current;
      pendingSendRef.current = false;
      autoSendRef.current = false;
      if (shouldSend) {
        sendPendingVoiceDraftRef.current();
        return;
      }
      if (wasCommandListen && voiceModeRef.current && !suspendedRef.current) {
        const now = Date.now();
        if (now - lastAutoRestartRef.current < MIN_AUTO_RESTART_INTERVAL_MS) {
          return;
        }
        lastAutoRestartRef.current = now;
        pendingCommandRestartRef.current = true;
        pendingRestartRef.current = true;
        setEndSignal((n) => n + 1);
        return;
      }
      // The recognizer stopped on its own (silence, a no-speech timeout, or a
      // network blip) before the gap timer fired. Keep the dialog alive instead
      // of freezing: send a draft the user already spoke, or reopen the mic.
      if (!wasAutoListening || !voiceModeRef.current || pausedRef.current || suspendedRef.current) {
        return;
      }
      if (draftRef.current.trim().length > 0) {
        sendPendingVoiceDraftRef.current();
        return;
      }
      const now = Date.now();
      if (now - lastAutoRestartRef.current < MIN_AUTO_RESTART_INTERVAL_MS) {
        return;
      }
      lastAutoRestartRef.current = now;
      // `start` is gated on a stale `listening` flag inside this end handler, so
      // the reopen waits for the recognizer to settle idle (see the effect).
      pendingRestartRef.current = true;
      setEndSignal((n) => n + 1);
    },
  });
  const voiceRef = useRef(voice);
  const { availability } = chat;
  const sendText = useAgentSend(chat, budget);

  const send = () => {
    const text = draft.trim();
    if (!text) {
      return;
    }
    pendingSendRef.current = false;
    voice.stop();
    stopSpeech();
    draftRef.current = "";
    setDraft("");
    sendText(text);
  };

  const sendDisabled =
    chat.streaming ||
    !draft.trim() ||
    availability === "unavailable" ||
    budget.level === "exhausted";
  const voiceDisabled =
    !voice.isSupported || availability === "unavailable" || budget.level === "exhausted";
  const inputDisabled = availability === "unavailable" || budget.level === "exhausted";
  const greeting = useSyncExternalStore(
    subscribeGreeting,
    getGreetingSpeaking,
    greetingServerSnapshot,
  );
  const speakingEntryId = useSyncExternalStore(
    subscribeSpeech,
    getSpeakingEntryId,
    idleSpeakingEntry,
  );
  const onVoiceActivityRef = useRef(onVoiceActivity);
  const onRevealVisualRef = useRef(onRevealVisual);
  const seenCommandRef = useRef<number | null>(null);
  const { listening, interimText, isSupported, error } = voice;
  const commandListen =
    voiceMode && (paused || greeting || speakingEntryId !== null || chat.streaming);
  const livePhase = voiceDialogPhase({
    voiceMode,
    listening,
    greeting,
    speaking: speakingEntryId !== null,
    streaming: chat.streaming,
    commandListen,
  });
  const voicePhase = paused ? pausedPhase : livePhase;

  // Closing the drawer keeps a voice dialog going. Expand, and plain dictation
  // with the drawer shut, still release the microphone so a hidden draft does
  // not keep growing.
  const { stop: stopVoice } = voice;
  useEffect(() => {
    if (suspended) {
      if (voiceModeRef.current || pausedRef.current || getGreetingSpeaking()) {
        stopSpeech();
      }
      voiceModeRef.current = false;
      pausedRef.current = false;
      resumeListeningRef.current = false;
      ignoreNextEndRef.current = false;
      gapDeadlineRef.current = null;
      gapRemainingRef.current = null;
      autoSendRef.current = false;
      pendingSendRef.current = false;
      helpOfferRef.current = false;
      commandListenRef.current = false;
      pendingCommandRestartRef.current = false;
      spokenLineRef.current = "";
      if (echoTailTimerRef.current !== null) {
        window.clearTimeout(echoTailTimerRef.current);
        echoTailTimerRef.current = null;
      }
      stopThinkingCue();
      stopVoice();
      return;
    }
    if (!open && !voiceModeRef.current) {
      autoSendRef.current = false;
      stopVoice();
    }
  }, [open, suspended, stopVoice]);

  // The floating mic owns this recognizer until the speaker pauses. Final and
  // interim results keep resetting the gap, so a breath does not send early.
  // Pausing the dialog freezes whatever time is left on that gap.
  useEffect(() => {
    if (paused || !listening || !autoSendRef.current) {
      return undefined;
    }
    if (draft.trim().length === 0 && interimText.trim().length === 0) {
      gapDeadlineRef.current = null;
      return undefined;
    }
    const now = Date.now();
    // Resuming a paused turn rebuilds the deadline from the time that was left,
    // so a long pause does not consume the gap and send a partial draft at once.
    gapDeadlineRef.current ??= now + (gapRemainingRef.current ?? voiceUtteranceGapMs(draft));
    gapRemainingRef.current = null;
    const delay = Math.max(0, gapDeadlineRef.current - now);
    const timer = window.setTimeout(() => {
      gapDeadlineRef.current = null;
      autoSendRef.current = false;
      pendingSendRef.current = true;
      voiceRef.current.stop();
    }, delay);
    return () => {
      window.clearTimeout(timer);
      if (!pausedRef.current) {
        gapDeadlineRef.current = null;
      }
    };
  }, [paused, listening, draft, interimText]);

  // Stable: sendPendingVoiceDraft closes over this and is kept on a ref.
  const leaveVoiceMode = useCallback(() => {
    voiceModeRef.current = false;
    setVoiceMode(false);
    pausedRef.current = false;
    setPaused(false);
    resumeListeningRef.current = false;
    ignoreNextEndRef.current = false;
    gapDeadlineRef.current = null;
    gapRemainingRef.current = null;
    autoSendRef.current = false;
    pendingSendRef.current = false;
    helpOfferRef.current = false;
    commandListenRef.current = false;
    pendingCommandRestartRef.current = false;
    spokenLineRef.current = "";
    if (echoTailTimerRef.current !== null) {
      window.clearTimeout(echoTailTimerRef.current);
      echoTailTimerRef.current = null;
    }
    voiceRef.current.stop();
    stopThinkingCue();
    stopSpeech();
    void releaseVoiceWakeLock();
  }, [setPaused, setVoiceMode]);

  const endVoiceDialog = useCallback(() => {
    if (!voiceModeRef.current) {
      return;
    }
    helpOfferRef.current = false;
    stopSpeech();
    speakOnce(VOICE_DIALOG_GOODBYE, leaveVoiceMode);
  }, [leaveVoiceMode]);

  // A denied or missing microphone makes the recognizer end immediately, so the
  // dialog would otherwise reopen the mic on the 1s floor forever with no sign
  // of why it is stuck. End the dialog once and say the reason out loud;
  // `leaveVoiceMode` runs first (it stops speech synchronously) so the notice is
  // not cancelled, and the composer mic tooltip keeps the error afterward.
  useEffect(() => {
    if (!voiceMode || error === null || !FATAL_VOICE_ERRORS.has(error)) {
      return;
    }
    pendingRestartRef.current = false;
    pendingCommandRestartRef.current = false;
    // Defer the teardown out of the effect body (react-hooks/set-state-in-effect).
    // The ref guard skips it if the dialog was already ended before this ran.
    queueMicrotask(() => {
      if (!voiceModeRef.current) {
        return;
      }
      leaveVoiceMode();
      speakOnce(VOICE_DIALOG_MIC_BLOCKED, () => undefined);
    });
  }, [voiceMode, error, leaveVoiceMode]);

  // Starting a new conversation or loading another one discards the turn this
  // dialog was driving. Without this the machine would stay in voice mode
  // listening only for commands, with no completed reply to reopen a user turn,
  // so ordinary requests would be ignored. End the dialog instead; the user
  // restarts voice on the fresh conversation when they want it.
  useEffect(() => {
    if (chat.conversationEpoch === conversationEpochRef.current) {
      return;
    }
    conversationEpochRef.current = chat.conversationEpoch;
    if (voiceModeRef.current) {
      leaveVoiceMode();
    }
  }, [chat.conversationEpoch, leaveVoiceMode]);

  // The pause button and a spoken "pause" share this. Spoken pause while the
  // assistant is talking must freeze TTS the same way as the button, and the
  // command mic stays open so "continue" can resume.
  const pauseVoiceDialog = useCallback(
    (source: "control" | "utterance" = "control") => {
      if (!voiceModeRef.current || pausedRef.current) {
        return;
      }
      const speakingNow = getGreetingSpeaking() || getSpeakingEntryId() !== null;
      const userListening = voiceRef.current.listening && !commandListenRef.current;
      const fromUserTurn = source === "utterance" && !commandListenRef.current && !speakingNow;
      const phase = fromUserTurn
        ? "listening"
        : voiceDialogPhase({
            voiceMode: true,
            listening: userListening,
            greeting: getGreetingSpeaking(),
            speaking: speakingNow,
            streaming: streamingRef.current,
            commandListen: commandListenRef.current,
          });
      setPausedPhase(phase);
      pausedRef.current = true;
      setPaused(true);
      // Freeze whatever time is left on the utterance gap so resume continues
      // it instead of restarting (or expiring) the countdown.
      if (gapDeadlineRef.current !== null) {
        gapRemainingRef.current = Math.max(0, gapDeadlineRef.current - Date.now());
        gapDeadlineRef.current = null;
      }
      if (speakingNow) {
        pauseSpeech();
      }
      stopThinkingCue();
      if (userListening || (source === "utterance" && phase === "listening")) {
        resumeListeningRef.current = true;
        pendingSendRef.current = false;
      }
      beginCommandListening(suspendedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
    },
    [setPaused],
  );

  const resumeVoiceDialog = useCallback(() => {
    if (!voiceModeRef.current || !pausedRef.current) {
      return;
    }
    pausedRef.current = false;
    setPaused(false);
    const speakingNow = getGreetingSpeaking() || getSpeakingEntryId() !== null;
    if (resumeListeningRef.current) {
      resumeListeningRef.current = false;
      stopThinkingCue();
      if (speakingNow) {
        resumeSpeech();
      }
      commandListenRef.current = false;
      autoSendRef.current = true;
      if (voiceRef.current.listening) {
        return;
      }
      beginListening(
        suspendedRef,
        pausedRef,
        voiceModeRef,
        autoSendRef,
        commandListenRef,
        voiceRef,
      );
      return;
    }
    const replyWaiting =
      !streamingRef.current &&
      entriesRef.current.some(
        (entry) =>
          entry.role === "assistant" &&
          liveReplyIdsRef.current.has(entry.id) &&
          !entry.streaming &&
          !entry.cancelled &&
          !spokenReplyIdsRef.current.has(entry.id),
      );
    if (replyWaiting) {
      stopThinkingCue();
      if (speakingNow) {
        stopSpeech();
      }
      return;
    }
    if (speakingNow) {
      resumeSpeech();
    }
    beginCommandListening(suspendedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
  }, [setPaused]);

  // The composer "Speak now" button and the floating mic above Play share this.
  // An open mic is already the outcome, so a second press leaves it alone.
  const listenInDialog = useCallback(() => {
    if (!voiceModeRef.current || voiceDisabled) {
      return;
    }
    pausedRef.current = false;
    setPaused(false);
    resumeListeningRef.current = false;
    gapDeadlineRef.current = null;
    pendingSendRef.current = false;
    stopSpeech();
    if (voiceRef.current.listening && !commandListenRef.current) {
      return;
    }
    beginListening(suspendedRef, pausedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
  }, [setPaused, voiceDisabled]);

  // Reopen the dialog mic with the standard auto-send listening settings and no
  // extra guards: every call site has already confirmed the dialog is live.
  // Refs only, so the identity is stable and it can sit in the effect
  // dependency arrays below without retriggering them.
  const beginDialogListening = useCallback(() => {
    beginListening(suspendedRef, pausedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
  }, []);

  const sendPendingVoiceDraft = useCallback(() => {
    const text = draftRef.current.trim();
    if (text.length === 0 || budgetLevelRef.current === "exhausted") {
      return;
    }
    setDraft(() => {
      draftRef.current = "";
      return "";
    });
    deliverSpokenUtterance(text, {
      voiceMode: voiceModeRef.current,
      awaitingHelpOffer: helpOfferRef.current,
      paused: false,
      assistantTurn: false,
      spokenLine: spokenLineRef.current,
      sendText: (value) => {
        sendTextRef.current(value, streamingRef.current ? { interrupt: true } : undefined);
      },
      leaveVoiceMode,
      pauseDialog: () => {
        pauseVoiceDialog("utterance");
      },
      resumeDialog: () => {
        resumeVoiceDialog();
      },
      interruptDialog: () => {
        listenInDialog();
      },
      setAwaitingHelpOffer: (awaiting) => {
        helpOfferRef.current = awaiting;
      },
    });
  }, [leaveVoiceMode, listenInDialog, pauseVoiceDialog, resumeVoiceDialog, setDraft]);

  const deliverCommand = useCallback(
    (text: string) => {
      deliverSpokenUtterance(text, {
        voiceMode: voiceModeRef.current,
        awaitingHelpOffer: helpOfferRef.current,
        paused: pausedRef.current,
        assistantTurn: !pausedRef.current,
        spokenLine: spokenLineRef.current,
        sendText: (value) => {
          sendTextRef.current(value, streamingRef.current ? { interrupt: true } : undefined);
        },
        leaveVoiceMode,
        pauseDialog: () => {
          pauseVoiceDialog("utterance");
        },
        resumeDialog: () => {
          resumeVoiceDialog();
        },
        interruptDialog: () => {
          listenInDialog();
        },
        setAwaitingHelpOffer: (awaiting) => {
          helpOfferRef.current = awaiting;
        },
      });
    },
    [leaveVoiceMode, listenInDialog, pauseVoiceDialog, resumeVoiceDialog],
  );

  useEffect(() => {
    draftRef.current = draft;
    streamingRef.current = chat.streaming;
    entriesRef.current = chat.entries;
    budgetLevelRef.current = budget.level;
    suspendedRef.current = suspended;
    voiceRef.current = voice;
    sendTextRef.current = sendText;
    onVoiceActivityRef.current = onVoiceActivity;
    onRevealVisualRef.current = onRevealVisual;
    sendPendingVoiceDraftRef.current = sendPendingVoiceDraft;
    deliverCommandRef.current = deliverCommand;
  }, [
    draft,
    chat.streaming,
    chat.entries,
    budget.level,
    suspended,
    voice,
    sendText,
    onVoiceActivity,
    onRevealVisual,
    sendPendingVoiceDraft,
    deliverCommand,
  ]);

  let activityTooltip = describeVoiceState({ isSupported, listening, error });
  if (voiceMode) {
    activityTooltip = VOICE_PHASE_LABEL[voicePhase];
  }
  if (paused) {
    activityTooltip = VOICE_RESUME_LABEL;
  }

  useEffect(() => {
    onVoiceActivityRef.current?.({
      listening: listening && !commandListen,
      greeting,
      voiceMode,
      paused,
      phase: voicePhase,
      disabled: voiceDisabled,
      tooltip: activityTooltip,
    });
  }, [
    greeting,
    listening,
    commandListen,
    isSupported,
    error,
    voiceDisabled,
    voiceMode,
    voicePhase,
    paused,
    activityTooltip,
  ]);

  // While the assistant is thinking, say one wait line, then a quiet pulse
  // until the reply is ready. The pulse stays off while that line is still
  // being spoken, the mic is open, or the dialog is paused. When the reply
  // that streamed in during this conversation is ready, read it and, unless it
  // already asks a question, ask a follow-up, then cue the mic. History loaded
  // from the server never streamed here, so it stays silent.
  useEffect(() => {
    if (!voiceMode) {
      holdingRef.current = false;
      revealedVisualIdsRef.current = new Set();
      pendingVisualFocusIdRef.current = null;
      stopThinkingCue();
      return;
    }
    for (const entry of chat.entries) {
      if (entry.role === "assistant" && entry.streaming) {
        liveReplyIdsRef.current.add(entry.id);
      }
    }
    if (pausedRef.current) {
      stopThinkingCue();
      return;
    }
    const startCommandListen = () => {
      beginCommandListening(suspendedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
    };
    if (chat.streaming) {
      advanceThinkingCue({
        listening,
        holdingRef,
        autoSendRef,
        pendingSendRef,
        streamingRef,
        voiceRef,
        commandListenRef,
        suspendedRef,
        voiceModeRef,
        pausedRef,
        spokenLineRef,
        echoTailTimerRef,
        startCommandListen,
      });
      return;
    }
    stopThinkingCue();
    holdingRef.current = false;
    const reply = chat.entries.find((entry) =>
      isUnspokenLiveReply(entry, liveReplyIdsRef, spokenReplyIdsRef),
    );
    if (reply === undefined) {
      return;
    }
    spokenReplyIdsRef.current.add(reply.id);
    const text = toSpeakableText(reply.text) || (reply.error ?? "").trim();
    autoSendRef.current = false;
    pendingSendRef.current = false;
    if (text.length === 0) {
      beginDialogListening();
      return;
    }
    const spoken = voiceDialogSpokenReply(text);
    // Arm the short-"no" closing whenever the spoken line asked a question,
    // whether that is the canned follow-up or the model's own question.
    helpOfferRef.current = voiceDialogOffersHelp(spoken);
    speakDialogLine(spoken, beginDialogListening, spokenLineRef, echoTailTimerRef, startCommandListen);
  }, [voiceMode, paused, listening, chat.entries, chat.streaming, beginDialogListening]);

  // A table, chart, or card is spoken as a cue. Open the drawer once per reply
  // so the user can see it, and remember that reply if they close the drawer.
  useEffect(() => {
    if (!voiceMode || suspended) {
      return;
    }
    for (const entry of chat.entries) {
      if (entry.role !== "assistant" || !liveReplyIdsRef.current.has(entry.id) || entry.cancelled) {
        continue;
      }
      if (revealedVisualIdsRef.current.has(entry.id) || !replyHasVisual(entry.text)) {
        continue;
      }
      revealedVisualIdsRef.current.add(entry.id);
      if (!open) {
        pendingVisualFocusIdRef.current = entry.id;
        onRevealVisualRef.current?.();
      }
    }
  }, [voiceMode, suspended, open, chat.entries]);

  // The drawer opens as soon as the visual's type is known. Scroll once the
  // node is actually in the transcript, then stop following the stream so later
  // tokens do not push the table or chart back off screen.
  useEffect(() => {
    if (!open) {
      return;
    }
    const entryId = pendingVisualFocusIdRef.current;
    if (entryId === null) {
      return;
    }
    const container = transcriptRef.current;
    if (!container) {
      return;
    }
    const root = container.querySelector(`[data-entry-id="${CSS.escape(entryId)}"]`);
    if (!(root instanceof HTMLElement)) {
      return;
    }
    const visual = root.querySelector("[data-agent-visual]");
    if (visual instanceof HTMLElement) {
      scrollTranscriptToTarget(container, visual);
      releaseStick();
      pendingVisualFocusIdRef.current = null;
      return;
    }
    const entry = chat.entries.find((item) => item.id === entryId);
    if (entry !== undefined && !entry.streaming) {
      scrollTranscriptToTarget(container, root);
      releaseStick();
      pendingVisualFocusIdRef.current = null;
    }
  }, [open, chat.entries, releaseStick, transcriptRef]);

  // Unmount only. Stopping the pulse from the thinking effect's cleanup would
  // cut it on every streamed token, and StrictMode's extra pass runs before a
  // hold line has been scheduled. The audio teardown is deferred to a microtask
  // so StrictMode's dev remount (which sets `mountedRef` true again) keeps the
  // greeting playing. The synthesizer is module-global, so it is only cancelled
  // when no drawer still owns the voice session: this instance clears its own
  // claim when it unmounts, and the microtask runs after that, so a live claim
  // means another mounted drawer took over and its speech must not be cut.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueMicrotask(() => {
        if (mountedRef.current || getVoiceDialogActive()) {
          return;
        }
        stopThinkingCue();
        stopSpeech();
      });
    };
  }, []);

  // While a voice conversation owns the mic, manual read-aloud elsewhere is
  // disabled so the two cannot drive the synthesizer at once. The flag is
  // module-global; clearing it on unmount re-enables read-aloud.
  useEffect(() => {
    setVoiceDialogActive(voiceMode);
    return () => {
      setVoiceDialogActive(false);
    };
  }, [voiceMode]);

  useEffect(() => {
    if (!voiceMode || paused) {
      void releaseVoiceWakeLock();
      return undefined;
    }
    void requestVoiceWakeLock();
    return () => {
      void releaseVoiceWakeLock();
    };
  }, [voiceMode, paused]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && voiceModeRef.current && !pausedRef.current) {
        // Stay paused after the tab returns. iOS cannot restart recognition
        // without a gesture; Resume / "continue" is the user's tap.
        pauseVoiceDialog();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pauseVoiceDialog]);

  // Reopen the mic after a silent natural end, once the recognizer has settled
  // idle. `start` no-ops while it still believes it is listening, so the reopen
  // cannot run inline in the end handler and waits for `listening` to clear.
  // `endSignal` is a dependency too: an interim-edit abort flips `listening`
  // false before the native end queues the restart, so without it this effect
  // would never re-run and the paused command mic would not reopen.
  useEffect(() => {
    if (voice.listening || !pendingRestartRef.current) {
      return;
    }
    pendingRestartRef.current = false;
    const commandRestart = pendingCommandRestartRef.current;
    pendingCommandRestartRef.current = false;
    if (commandRestart) {
      if (!voiceModeRef.current || suspendedRef.current) {
        return;
      }
      beginCommandListening(suspendedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
      return;
    }
    if (!dialogLive(suspendedRef, pausedRef, voiceModeRef)) {
      return;
    }
    autoSendRef.current = true;
    commandListenRef.current = false;
    // `voiceRef` is the latest recognizer (the sync effect above runs first),
    // so this reopens with a `start` that no longer believes it is listening.
    voiceRef.current.start();
  }, [voice.listening, endSignal]);

  // Commands are events. `seenCommandRef` survives StrictMode's extra effect
  // pass so the greeting is not cancelled and then skipped. Closing the drawer
  // does not forget the id, so reopening it does not start another chat.
  useEffect(() => {
    if (voiceCommand === null || seenCommandRef.current === voiceCommand.id) {
      return;
    }
    if (suspended) {
      seenCommandRef.current = voiceCommand.id;
      return;
    }
    seenCommandRef.current = voiceCommand.id;

    const clearPaused = () => {
      pausedRef.current = false;
      setPaused(false);
      resumeListeningRef.current = false;
      ignoreNextEndRef.current = false;
      gapDeadlineRef.current = null;
      gapRemainingRef.current = null;
      helpOfferRef.current = false;
    };
    const enterVoiceMode = () => {
      if (!voiceModeRef.current) {
        liveReplyIdsRef.current = new Set();
        spokenReplyIdsRef.current = new Set();
        resetHoldPhraseCooldown();
      }
      clearPaused();
      voiceModeRef.current = true;
      setVoiceMode(true);
    };
    const startCommandListen = () => {
      beginCommandListening(suspendedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
    };
    if (voiceCommand.action === "exit") {
      endVoiceDialog();
      return;
    }
    if (voiceCommand.action === "pause") {
      pauseVoiceDialog();
      return;
    }
    if (voiceCommand.action === "resume") {
      resumeVoiceDialog();
      return;
    }
    if (voiceCommand.action === "listen") {
      listenInDialog();
      return;
    }
    if (voiceDisabled) {
      return;
    }

    const clearDraft = () => {
      setDraft(() => {
        draftRef.current = "";
        return "";
      });
    };

    if (voiceCommand.action === "send") {
      pendingSendRef.current = true;
      if (voiceRef.current.listening) {
        voiceRef.current.stop();
        return;
      }
      pendingSendRef.current = false;
      sendPendingVoiceDraftRef.current();
      return;
    }

    if (voiceCommand.action === "interrupt") {
      enterVoiceMode();
      stopSpeech();
      beginDialogListening();
      return;
    }

    enterVoiceMode();
    // Stop any composer dictation before the greeting so its open recognizer
    // cannot fold the spoken greeting into the freshly cleared draft.
    if (voiceRef.current.listening) {
      ignoreNextEndRef.current = true;
      voiceRef.current.stop();
    }
    stopSpeech();
    clearDraft();
    if (voiceCommand.skipGreeting) {
      beginDialogListening();
      return;
    }
    helpOfferRef.current = true;
    speakDialogLine(
      voiceDialogOpeningLine(voiceDialogHasTranscript(entriesRef.current)),
      beginDialogListening,
      spokenLineRef,
      echoTailTimerRef,
      startCommandListen,
    );
  }, [
    voiceCommand,
    suspended,
    voiceDisabled,
    setDraft,
    pauseVoiceDialog,
    resumeVoiceDialog,
    listenInDialog,
    endVoiceDialog,
    beginDialogListening,
  ]);

  if (suspended && voiceMode) {
    setVoiceMode(false);
  }
  if (suspended && paused) {
    setPaused(false);
  }

  const startVoiceDialog = () => {
    if (voiceDisabled) {
      return;
    }
    primeListenCue();
    resetHoldPhraseCooldown();
    liveReplyIdsRef.current = new Set();
    spokenReplyIdsRef.current = new Set();
    helpOfferRef.current = true;
    pausedRef.current = false;
    setPaused(false);
    gapDeadlineRef.current = null;
    gapRemainingRef.current = null;
    voiceModeRef.current = true;
    setVoiceMode(true);
    // Hand the mic to the dialog cleanly: stop any composer dictation first so
    // its open recognizer cannot transcribe the greeting into the fresh draft.
    if (voiceRef.current.listening) {
      ignoreNextEndRef.current = true;
      voiceRef.current.stop();
    }
    stopSpeech();
    setDraft(() => {
      draftRef.current = "";
      return "";
    });
    speakDialogLine(
      voiceDialogOpeningLine(voiceDialogHasTranscript(entriesRef.current)),
      () => {
        beginListening(
          suspendedRef,
          pausedRef,
          voiceModeRef,
          autoSendRef,
          commandListenRef,
          voiceRef,
        );
      },
      spokenLineRef,
      echoTailTimerRef,
      () => {
        beginCommandListening(suspendedRef, voiceModeRef, autoSendRef, commandListenRef, voiceRef);
      },
    );
  };

  const toggleVoiceDialog = () => {
    if (!voiceModeRef.current) {
      startVoiceDialog();
      return;
    }
    if (pausedRef.current) {
      resumeVoiceDialog();
      return;
    }
    pauseVoiceDialog();
  };

  return {
    draft,
    setDraft,
    send,
    sendText,
    voice,
    inputDisabled,
    sendDisabled,
    voiceDisabled,
    voicePhase,
    paused,
    voiceConversationDisabled: voiceDisabled && voicePhase === "idle",
    activityTooltip,
    toggleVoiceDialog,
    listenInDialog,
    leaveVoiceMode,
    endVoiceDialog,
  };
}
